import { useState, useEffect, useMemo } from "react";
import { listTenants, deleteTenant, listPartners, getTenantChannelStatus } from "../lib/api";
import type { Tenant, Partner } from "../types";
import type { TenantChannelState } from "../lib/api";
import { useNavigate } from "react-router-dom";
import Pager, { PAGE_SIZE } from "./Pager";

function llmProviderLabel(t: Pick<Tenant, "llm_provider" | "plan_tier" | "premium_until">): string {
  // 有效引擎照 ai-core resolveEngineTier／resolveEffectiveEngine 口徑（2026-11-03 Captain 令——
  // 列表顯示實際編排引擎，唔再顯示 llm_provider 靜態後備值「CF Workers AI」）：
  // legacy／pro499／trial 層＝Premium＝DeepSeek 主力；lite199 期內＝DeepSeek、期滿＝GLM（Basic）；
  // 租戶自訂 provider（經編輯頁設定）→ 原樣標「租戶自訂」
  if (t.llm_provider && t.llm_provider !== "cf-workers-ai") {
    const 自訂: Record<string, string> = { deepseek: "DeepSeek", glm: "GLM" };
    return `${自訂[t.llm_provider] ?? t.llm_provider}（租戶自訂）`;
  }
  const tier = t.plan_tier ?? "";
  const premium =
    tier === "pro499" || tier === "legacy" ||
    (tier === "lite199" && !!t.premium_until && Date.now() < t.premium_until);
  return premium ? "DeepSeek 主力（Premium）" : "GLM（Basic）";
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  // 渠道篩選（P1 §2.3）：''＝全部／'__none__'＝無夥伴／其他＝夥伴 id
  const [channel, setChannel] = useState("");
  const [page, setPage] = useState(0);
  // Captain 2026-10-9：每頁筆數可選（10/20/50/100）＋Global Search
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  // 刪除租戶確認對話框（Captain 2026-10-23 方案 A）：明示勾選（預設不勾）先釋放 CloudWAPI 通道
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);
  const [releaseChecked, setReleaseChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState("");
  // 通道實況（2026-10-28 三系統協調）：按行按需查詢，唔好頁面載入就 N 個上游呼叫
  const [liveStatus, setLiveStatus] = useState<Record<string, { state: TenantChannelState; channel?: string } | "loading">>({});
  async function probeLive(t: Tenant) {
    setLiveStatus((prev) => ({ ...prev, [t.tenant_id]: "loading" }));
    try {
      const r = await getTenantChannelStatus(t.tenant_id);
      setLiveStatus((prev) => ({
        ...prev,
        [t.tenant_id]: { state: r.success ? r.state : "unknown", channel: r.channel },
      }));
    } catch {
      setLiveStatus((prev) => ({ ...prev, [t.tenant_id]: { state: "unknown" } }));
    }
  }

  useEffect(() => {
    loadTenants();
  }, []);

  // 篩選變更時重置到第一頁
  useEffect(() => {
    setPage(0);
  }, [channel]);

  async function loadTenants() {
    setLoading(true);
    setError("");
    try {
      const [tData, pData] = await Promise.all([listTenants(), listPartners().catch(() => ({ partners: [], featureDefs: [] }))]);
      if (tData.success) {
        setTenants(tData.tenants || []);
        setPartners(pData.partners || []);
      } else {
        setError(tData.error || "載入失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }

  const partnerName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of partners) map[p.partner_id] = p.name;
    return map;
  }, [partners]);

  const filtered = useMemo(() => {
    let base = tenants;
    if (channel === "__none__") base = base.filter((t) => !t.partner_id);
    else if (channel) base = base.filter((t) => t.partner_id === channel);
    // Global Search（Captain 2026-10-9）：跨 tenant_id/bot_name/provider/plan/phone
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      base = base.filter((t) =>
        [t.tenant_id, t.bot_name, t.llm_provider, t.plan_type, t.plan_tier, t.whatsapp_sender_number, t.contact_handoff]
          .some((v) => (v || "").toString().toLowerCase().includes(q))
      );
    }
    return base;
  }, [tenants, channel, search]);

  const pageData = useMemo(
    () => filtered.slice(page * pageSize, (page + 1) * pageSize),
    [filtered, page, pageSize]
  );

  // CSV 導出（Captain 2026-10-9）：導出過濾後全部租戶
  function exportCsv() {
    const rows = [
      ["tenant_id", "bot_name", "plan_type", "plan_tier", "llm_provider", "whatsapp_sender", "kb_chunks", "partner_id"],
      ...filtered.map((t) => [t.tenant_id, t.bot_name || "", t.plan_type || "", t.plan_tier || "", t.llm_provider || "", t.whatsapp_sender_number || "", String(t.kb_chunk_count ?? ""), t.partner_id || ""]),
    ];
    const csv = rows.map((r) => r.map((c) => '"' + (c || "").replace(/"/g, '""') + '"').join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tenants-" + new Date().toISOString().slice(0, 10) + ".csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function openDeleteDialog(t: Tenant) {
    setDeleteTarget(t);
    setReleaseChecked(false); // 預設不勾——必須明示選擇才釋放通道
    setNotice("");
    setError("");
  }

  async function confirmDelete() {
    const t = deleteTarget;
    if (!t || deleting) return;
    setDeleting(true);
    setError("");
    try {
      const data = await deleteTenant(t.tenant_id, { releaseChannel: releaseChecked });
      if (data.success) {
        setTenants((prev) => prev.filter((x) => x.tenant_id !== t.tenant_id));
        // 通道釋放結果必須明確顯示（成功／失敗原因），不得靜默
        const cr = data.channel_release;
        if (cr?.attempted && cr.ok) {
          setNotice(`已刪除租戶「${t.tenant_id}」，CloudWAPI 通道已一併釋放。`);
        } else if (cr?.attempted && !cr.ok) {
          setNotice(`已刪除租戶「${t.tenant_id}」，但 CloudWAPI 通道釋放失敗：${cr.detail}——請到 CloudWAPI 後台人手處理。`);
        } else if (cr && !cr.attempted) {
          setNotice(`已刪除租戶「${t.tenant_id}」，但未能釋放通道：${cr.detail}`);
        } else {
          setNotice(`已刪除租戶「${t.tenant_id}」。`);
        }
        setDeleteTarget(null);
      } else {
        setError(data.error || "刪除失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">🏢 租戶列表</h1>
          <p className="text-xs text-slate-500 mt-1">新增租戶僅供內部／特殊直建（無訂單）；一般付款開通請到「開通中心」。</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/tenants/create")}
            title="內部／特殊直建（無訂單）"
            className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
          >
            ➕ 新增租戶
          </button>
          <button
            onClick={loadTenants}
            className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
          >
            🔄 重新載入
          </button>
        </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="🔍 Global Search：ID / BOT 名稱 / 引擎 / 方案 / WhatsApp"
          className="flex-1 min-w-[280px] px-4 py-2 text-sm bg-slate-900 border border-slate-700 rounded-lg text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-emerald-600"
        />
        <div className="flex items-center gap-2 text-sm text-slate-400">
          每頁
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          筆
          <button onClick={exportCsv} className="px-3 py-1.5 text-xs bg-sky-700 hover:bg-sky-600 text-white rounded-lg transition-colors">📥 導出 CSV</button>
        </div>
      </div>
      </div>

      {notice && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-emerald-600/40 bg-emerald-600/10 px-4 py-3 text-sm text-emerald-300">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-emerald-500 hover:text-emerald-300">✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">載入中...</div>
      ) : error ? (
        <div className="text-rose-400">❌ {error}</div>
      ) : (
        <div className="overflow-x-auto">
          {/* 渠道篩選（P1 §2.3）：全部／無夥伴／各夥伴 */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs text-slate-500">渠道：</span>
            <button
              onClick={() => setChannel("")}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                channel === ""
                  ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/40"
                  : "bg-slate-900 text-slate-400 border-slate-700 hover:text-slate-200"
              }`}
            >
              全部
            </button>
            <button
              onClick={() => setChannel("__none__")}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                channel === "__none__"
                  ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/40"
                  : "bg-slate-900 text-slate-400 border-slate-700 hover:text-slate-200"
              }`}
            >
              無夥伴
            </button>
            {partners.map((p) => (
              <button
                key={p.partner_id}
                onClick={() => setChannel(p.partner_id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  channel === p.partner_id
                    ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/40"
                    : "bg-slate-900 text-slate-400 border-slate-700 hover:text-slate-200"
                }`}
              >
                🤝 {p.name}
              </button>
            ))}
          </div>
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
              <tr>
                <th className="px-4 py-3">租戶 ID</th>
                <th className="px-4 py-3">Bot 名稱</th>
                <th className="px-4 py-3">渠道</th>
                <th className="px-4 py-3">方案</th>
                <th className="px-4 py-3">定價層級</th>
                <th className="px-4 py-3">LLM 提供商</th>
                <th className="px-4 py-3">API Key</th>
                <th className="px-4 py-3">WhatsApp<span className="block text-[10px] font-normal text-slate-600">✅＝有設定（非在線）；在線請按「實況」</span></th>
                <th className="px-4 py-3">知識庫</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((t) => (
                <tr
                  key={t.tenant_id}
                  className="bg-slate-900/50 border-b border-slate-800 hover:bg-slate-800/50"
                >
                  <td className="px-4 py-3 font-mono text-emerald-400 text-xs">
                    {t.tenant_id}
                  </td>
                  <td className="px-4 py-3">{t.bot_name || "—"}</td>
                  <td className="px-4 py-3">
                    {t.partner_id ? (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-500/20 text-violet-300" title={t.partner_id}>
                        🤝 {partnerName[t.partner_id] || t.partner_id}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        t.plan_type === "paid"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : t.plan_type === "trial"
                          ? "bg-amber-500/20 text-amber-400"
                          : "bg-rose-500/20 text-rose-400"
                      }`}
                    >
                      {t.plan_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        t.plan_tier === "pro499" || t.plan_tier === "legacy"
                          ? "bg-sky-500/20 text-sky-400"
                          : t.plan_tier === "trial99" || t.plan_tier === "trial99i"
                          ? "bg-violet-500/20 text-violet-300"
                          : "bg-lime-500/20 text-lime-400"
                      }`}
                    >
                      {t.plan_tier === "lite199"
                        ? "Lite"
                        : t.plan_tier === "pro499"
                        ? "Pro"
                        : t.plan_tier === "trial99" || t.plan_tier === "trial99i"
                        ? "Trial"
                        : "Legacy"}
                      {t.plan_tier === "trial99" || t.plan_tier === "trial99i"
                        ? t.trial_ends_at
                          ? ` · ${new Date(t.trial_ends_at).toLocaleString("zh-HK", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })} 到期`
                          : " · 待開通"
                        : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">
                    {llmProviderLabel(t)}
                  </td>
                  <td className="px-4 py-3">
                    {t.llm_provider === "deepseek" && t.llm_api_key ? (
                      <span className="inline-flex items-center gap-1 text-xs text-sky-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                        自訂 Key
                      </span>
                    ) : t.llm_provider === "deepseek" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        系統預設
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.whatsapp_sender_number ? (
                      <div className="flex flex-col gap-1 items-start">
                        {/* ✅＝「已填寫設定」，唔代表通道在線；在線與否用「實況」即場查 */}
                        <span className="text-xs text-emerald-400" title="✅ 代表已填寫設定，不代表通道在線——請用「實況」即場查詢">
                          ✅ {t.whatsapp_sender_number}
                        </span>
                        {(() => {
                          const lv = liveStatus[t.tenant_id];
                          if (lv === "loading") {
                            return <span className="text-[11px] text-slate-500">查詢中…</span>;
                          }
                          if (lv) {
                            const map: Record<TenantChannelState, { cls: string; label: string }> = {
                              connected: { cls: "text-emerald-400", label: `● 在線${lv.channel ? `（${lv.channel}）` : ""}` },
                              disconnected: { cls: "text-amber-400", label: "● 離線" },
                              not_found: { cls: "text-rose-400", label: "● 通道已刪除" },
                              not_configured: { cls: "text-slate-500", label: "未設定" },
                              unknown: { cls: "text-slate-500", label: "未能確認（上游異常）" },
                            };
                            const m = map[lv.state] || map.unknown;
                            return <span className={`text-[11px] ${m.cls}`}>{m.label}</span>;
                          }
                          return (
                            <button
                              onClick={() => probeLive(t)}
                              className="px-2 py-0.5 text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-400 rounded border border-slate-700 transition-colors"
                            >
                              實況
                            </button>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">未設定</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{t.kb_chunk_count} chunks</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/tenants/edit/${t.tenant_id}`)}
                        className="px-3 py-1 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded transition-colors"
                      >
                        ✏️ 編輯
                      </button>
                      <button
                        onClick={() => openDeleteDialog(t)}
                        className="px-3 py-1 text-xs bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 rounded transition-colors"
                      >
                        🗑️ 刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            page={page}
            totalPages={Math.max(1, Math.ceil(filtered.length / pageSize))}
            total={filtered.length}
            onChange={setPage}
          />
        </div>
      )}

      {/* 刪除租戶確認對話框：明示勾選（預設不勾）才釋放 CloudWAPI 通道（Captain 2026-10-23 方案 A） */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-slate-100 mb-3">🗑️ 刪除租戶</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              確定要刪除租戶「<span className="font-mono text-rose-400">{deleteTarget.tenant_id}</span>」嗎？
              此操作會一併刪除對話記錄、知識庫與功能設定，且無法復原。
            </p>
            <label className="mt-4 flex items-start gap-3 rounded-lg border border-amber-600/40 bg-amber-600/10 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={releaseChecked}
                onChange={(e) => setReleaseChecked(e.target.checked)}
                disabled={deleting}
                className="mt-1 h-4 w-4 accent-amber-500"
              />
              <span className="text-sm text-amber-200 leading-relaxed">
                同時釋放 CloudWAPI 通道（商戶端刪除 WhatsApp 裝置
                {deleteTarget.whatsapp_sender_number ? (
                  <> <span className="font-mono">{deleteTarget.whatsapp_sender_number}</span></>
                ) : null}
                ＋釋放配額，不可復原）
              </span>
            </label>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? "刪除中…" : releaseChecked ? "確認刪除＋釋放通道" : "確認刪除"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
